/**
 * useResize Hook
 * 
 * 监听元素尺寸变化，自动计算宽高
 */
import { useState, useEffect, useCallback, RefObject } from 'react';

const DEFAULT_RATIO = 2;
const DEFAULT_MIN_WIDTH = 344;
const DEFAULT_MIN_HEIGHT = 210;
const DEFAULT_MAX_HEIGHT = 500;

export interface UseResizeOptions {
  /** 宽高比，默认 2 */
  ratio?: number;
  /** 最小宽度，默认 344 */
  minWidth?: number;
  /** 最小高度，默认 210 */
  minHeight?: number;
  /** 最大高度，默认 500 */
  maxHeight?: number;
  /** 动态获取最小宽度的函数 */
  getMinWidthFunc?: () => number;
}

export interface UseResizeResult {
  /** 计算后的宽高 */
  rect: { width: number; height: number };
  /** 是否正在加载 */
  isLoading: boolean;
  /** 设置加载状态 */
  setLoading: (loading: boolean) => void;
}

export default function useResize(
  elRef: RefObject<HTMLElement | null>,
  options: UseResizeOptions = {}
): UseResizeResult {
  const {
    ratio = DEFAULT_RATIO,
    minWidth = DEFAULT_MIN_WIDTH,
    minHeight = DEFAULT_MIN_HEIGHT,
    maxHeight = DEFAULT_MAX_HEIGHT,
    getMinWidthFunc,
  } = options;

  const [isLoading, setLoading] = useState(false);
  const [rect, setRect] = useState({
    width: minWidth,
    height: minHeight,
  });

  const handleResize = useCallback(() => {
    if (!elRef.current) return;

    const { width } = elRef.current.getBoundingClientRect();
    const dynamicMinWidth = getMinWidthFunc ? getMinWidthFunc() : minWidth;

    const calculateWidth = width < dynamicMinWidth ? dynamicMinWidth : width;

    let calculateHeight = calculateWidth / ratio;
    if (calculateHeight < minHeight) {
      calculateHeight = minHeight;
    }
    if (calculateHeight > maxHeight) {
      calculateHeight = maxHeight;
    }

    setRect({
      width: calculateWidth,
      height: calculateHeight,
    });
    setLoading(false);
  }, [ratio, minWidth, minHeight, maxHeight, getMinWidthFunc, elRef]);

  useEffect(() => {
    if (!elRef.current) return;

    setLoading(true);

    // 使用 ResizeObserver 监听尺寸变化
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(elRef.current);

    // 初始计算
    handleResize();

    return () => {
      resizeObserver.disconnect();
    };
  }, [elRef, handleResize]);

  return { rect, isLoading, setLoading };
}
